import { getRecaptchaToken, loadRecaptchaScript, hideRecaptchaBadge } from "../utils/recaptcha";
import React, { useEffect, useState } from 'react';
import { useLocation } from 'react-router-dom';
import UserForm from './UserForm.js';
import './QrCodePage.css';
import './CustomerTrackingPage.css';
import EntryScreen from './EntryScreen.js';
const apiUrl = process.env.REACT_APP_API_URL;
const BASE_URL = `${apiUrl}/business`;

const UserPage = () => {
  const location = useLocation();
  const [businessId, setBusinessId] = useState(null);
  const [businessInfo, setBusinessInfo] = useState(null);
  const [businessMetadata, setBusinessMetadata] = useState(null);
  const [enteredUser, setEnteredUser] = useState(null);
  const [businessName, setBusinessName] = useState(null);
  const [queueInfo, setQueueInfo] = useState(null);
  const [userInfo, setUserInfo] = useState(null);
  const [loading, setLoading] = useState(true);
  const [customerName, setCustomerName] = useState(null);
  const [business_info_all, setBusinessInfoAll] = useState(null);
  const [isReservation, setIsReservation] = useState(false);
  const [reservedQueue, setReservedQueue] = useState(null);

  useEffect(() => {
    loadRecaptchaScript();
    return () => {
      hideRecaptchaBadge();
    };
  }, []);

  useEffect(() => {
    const queryParams = new URLSearchParams(location.search);
    const uuid = queryParams.get('key') || queryParams.get('uuid');
    const businessIdParam = queryParams.get('business_id');
    const isReservationParam = queryParams.get('type') === 'reservation';
    setIsReservation(isReservationParam);

    const targetUrl = uuid
      ? `${BASE_URL}/validate_qr?key=${uuid}`
      : businessIdParam
      ? `${BASE_URL}/business_info?business_id=${businessIdParam}`
      : null;

    if (targetUrl) {
      const fetchBusinessInfo = async () => {
        try {
          const response = await fetch(targetUrl);
          const result = await response.json();
          if (result.data) {
            const resolvedBusinessId = result.data.business_id 
              || result.data.queue_info?.[0]?.business_id 
              || (businessIdParam ? parseInt(businessIdParam, 10) : null);

            setBusinessId(resolvedBusinessId);
            setBusinessInfo(result.data.queue_info[0]);
            setBusinessInfoAll(result.data);
            setBusinessMetadata(result.data.business_metadata);
            
            const foundReserved = result.data.queue_info.find(queue => queue.queue_type === 'reserved');
            setReservedQueue(foundReserved || null);

            const filteredQueueInfo = result.data.queue_info.filter(queue => queue.queue_type !== 'reserved');
            setQueueInfo(filteredQueueInfo);
            setBusinessName(result.data.business_name);
          } else {
            console.error('Invalid response from API:', result);
          }
        } catch (error) {
          console.error('Error fetching business information:', error);
        } finally {
          setLoading(false);
        }
      };

      fetchBusinessInfo();
    } else {
      setLoading(false);
    }
  }, [location]);

  const handleSubmit = async (formData) => {
    const queryParams = new URLSearchParams(location.search);
    const businessIdParam = queryParams.get('business_id');

    let recaptchaToken = null;
    try {
      recaptchaToken = await getRecaptchaToken('enqueue_user');
    } catch (err) {
      console.error('reCAPTCHA token generation error:', err);
      alert(err.message || 'Security verification timed out due to slow network. Please try submitting again.');
      return;
    }

    if (!recaptchaToken) {
      alert('Security verification token is missing. Please try submitting again.');
      return;
    }

    const dataToSubmit = {
      ...formData,
      business_id: businessId || (businessIdParam ? parseInt(businessIdParam, 10) : null), // Fallback to URL business_id
      recaptcha_token: recaptchaToken,
    };

    console.log('Form submitted:', dataToSubmit);

    try {
      const response = await fetch(`${BASE_URL}/enqueue_user`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(dataToSubmit),
      });

      if (response.ok) {
        const result = await response.json();
        console.log('User enqueued successfully:', result);

        if (result.success) {
          if (result.data.queue_info[0].status === 'active') {
            window.location.href = result.data.unique_url;
          } else if (result.data.queue_info[0].status === 'entered') {
            setCustomerName(result.data.queue_info[0].customer_name);
            setUserInfo(result.data.queue_info[0])
            setEnteredUser(true);
          }
        }
      } else {
        console.error('Error enqueuing user:', response.statusText);
      }
    } catch (error) {
      console.error('Network error:', error);
    }
  };

  if (loading) {
    return <div className="loader"></div>; // Show loader while fetching data
  }
  if (enteredUser) {
    return <EntryScreen business_info={business_info_all} customer_info={userInfo} />;
  }

  return (
    <div>
      {businessMetadata && (
        <div>
          <div className="top-bar">
            <img src="https://i.ibb.co/Nr7qRgH/Waitwhiz-Logo-Pure-White-Background.png" alt="Waitwhiz Logo" className="waitwhiz-logo" />
            <img src={businessMetadata.logo_url} alt="Business Logo" className="business-logo" />
          </div>

          <div style={{ textAlign: 'center', flex: 1 }}>
            <p>
              For Menu,{' '}
              <a
                href={businessMetadata.menu_url}
                target="_blank"
                rel="noopener noreferrer"
              >
                click here
              </a>
              .
            </p>
            <h3>{isReservation ? 'Please fill in the form for reservation' : 'Please fill in the form for entry'}</h3>
          </div>
        </div>
      )}
      {businessInfo ? (
        <div style={{ padding: '0 20px' }}>
          <UserForm
            onSubmit={handleSubmit}
            allentered_check={false}
            queue_info={queueInfo}
            auto_queue={businessMetadata.automatic_queue_selection}
            usercheck={true}
            isPhoneRequired={true}
            business_id={businessId}
            business_type={business_info_all?.business_type || ""}
            isReservation={isReservation}
            reservedQueue={reservedQueue}
          />
          {!isReservation && queueInfo && (
            <div className="disclaimer">
              {queueInfo.map((queue, index) => (
                <div key={index} style={{ marginBottom: '20px' }}>
                  <h4>{queue.queue_name}</h4>
                  {queue.max_waiting_number <= 1 ? (
                    <p>There is no waiting going on.</p>
                  ) : (
                    <>
                      <p>
                        Estimated Wait time:&nbsp;
                        <strong>
                          {queue.estimated_wait_time_buffer === 0
                            ? `${queue.total_initial_waiting_time}`
                            : `${queue.total_initial_waiting_time}-${queue.total_initial_waiting_time + queue.estimated_wait_time_buffer}`}
                        </strong>
                        &nbsp;minutes.
                      </p>
                      <p>People already in the queue: <strong>{queue.max_waiting_number - 1}</strong></p>
                    </>
                  )}
                </div>
              ))}
            </div>
          )}

          {businessMetadata.dos_and_donts && (
            <div className="DosAndDonts">
              <p>
                {businessMetadata.dos_and_donts}
              </p>
            </div>
          )
          }

        </div>
      ) : (
        <div className="loader"></div>
      )}
    </div>
  );
};

export default UserPage;
